import { ActionIcon, Box, Menu, Tooltip } from '@mantine/core';
import {
  IconDotsVertical,
  IconTransfer,
  IconTrash,
  IconUserMinus,
  IconUserShare,
} from '@tabler/icons-react';
import { useState } from 'react';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { ClientListItem } from 'src/admin/users/types';
import { useUpdateClient } from 'src/clients/hooks/useUpdateClient';
import TransferClientModal from 'src/home/TransferClientModal';

type Props = {
  client: ClientListItem;
  onDelete: () => void;
  onRemoveFromFamily?: () => void;
  iconSize?: number;
  iconColor?: string;
};

export function ClientActionsMenu({
  client,
  onDelete,
  onRemoveFromFamily,
  iconSize = 20,
  iconColor,
}: Props) {
  const { currentUser, isAdvisor } = useCurrentUser();
  const { updateClient } = useUpdateClient();
  const [transferOpen, setTransferOpen] = useState(false);

  // Family members inherit the family's advisor, so a client in a family can
  // only be moved by transferring the whole family, never on their own.
  const inFamily = (client.familyId ?? client.family?.id) != null;
  const canTransfer = currentUser != null && !isAdvisor && !inFamily;

  const handleSetToAdvised = async () => {
    await updateClient(client.id, {
      advised: true,
      familyId: client.familyId ?? client.family?.id,
      advisorUserId: client.advisorUserId,
    });
  };

  return (
    <Box ta="right">
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray">
            <IconDotsVertical size={iconSize} color={iconColor} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {canTransfer && (
            <Menu.Item
              leftSection={<IconUserShare size={14} />}
              onClick={() => setTransferOpen(true)}
            >
              Transfer
            </Menu.Item>
          )}
          <Menu.Item
            leftSection={<IconTransfer size={14} />}
            onClick={handleSetToAdvised}
            disabled={client.advised}
          >
            {client.advised ? (
              <Tooltip label="Setting to non-advised is currently not supported via the platform. Please contact support.">
                <span>Set to non-advised</span>
              </Tooltip>
            ) : (
              'Set to advised'
            )}
          </Menu.Item>
          {onRemoveFromFamily && (
            <Menu.Item leftSection={<IconUserMinus size={14} />} onClick={onRemoveFromFamily}>
              Remove from family
            </Menu.Item>
          )}
          <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete}>
            Delete client
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      {transferOpen && (
        <TransferClientModal
          currentAdvisorId={client.advisorUserId}
          clientId={client.id}
          opened={true}
          close={() => setTransferOpen(false)}
        />
      )}
    </Box>
  );
}
